import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from 'react-toastify';
import { X, User2, Edit2, Save, Ban, Unlock, Mic, Square, Send, Trash2, Loader2, Camera } from "lucide-react";
import ConversationMessages from "../components/conversations/ConversationMessages.tsx";
import { fetchMessages, sendMessage, markAsSeen, switchToHuman } from "../../../../services/api.js";
import { fetchShortcuts, updateClientNickname, blockContactByPhone, unblockContactByPhone } from '../services/api.ts';
import { CONVERSATION_MESSAGES_POLL_MS } from "../constants/timing.js";
import { getApiErrorMessage } from "../../../../utils/apiError";
import { formatDuration, MAX_AUDIO_DURATION_MS } from "../utils/mediaHelpers.js";
import { useChatMedia } from "../hooks/useChatMedia.ts";
import { useMessageImprovements } from "../hooks/useMessageImprovements.ts";
import { useOptimisticMessages } from "../hooks/useOptimisticMessages.ts";
import { getAvatarInitial, useClientAvatar } from "../hooks/useClientAvatar.ts";

interface Shortcut {
  id: number;
  atalho: string;
  mensagem: string;
  criado_em: string;
}

type SideModalProps = {
  conv: any;
  onClose: () => void;
};

const formatPhone = (phone?: string): string => {
  if (!phone) return "";
  const digits = phone.replace(/\D/g, "");
  if (!digits) return phone;

  if (digits.startsWith("55") && digits.length >= 12) {
    const country = digits.slice(0, 2);
    const area = digits.slice(2, 4);
    const rest = digits.slice(4);
    const isMobile = rest.length >= 9;
    const prefix = isMobile ? rest.slice(0, 5) : rest.slice(0, 4);
    const suffix = isMobile ? rest.slice(5, 9) : rest.slice(4, 8);
    return `+${country} (${area}) ${prefix}-${suffix}`;
  }

  if (digits.length >= 10) {
    const area = digits.slice(0, 2);
    const rest = digits.slice(2);
    const prefix = rest.length >= 9 ? rest.slice(0, 5) : rest.slice(0, 4);
    const suffix = rest.length >= 9 ? rest.slice(5, 9) : rest.slice(4, 8);
    return `(${area}) ${prefix}-${suffix}`;
  }

  return phone;
};

const SideModal = ({ conv, onClose }: SideModalProps) => {
  const { messages, addPendingMessage, confirmMessage, removePendingMessage, receiveMessages } = useOptimisticMessages();
  const [newMessage, setNewMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  const [isBotLocal, setIsBotLocal] = useState(conv?.type === "bot");
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [isBlocking, setIsBlocking] = useState(false);
  const [blockedOverride, setBlockedOverride] = useState<boolean | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const activeConversationIdRef = useRef(conv?.id);
  activeConversationIdRef.current = conv?.id;
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isUserNearBottom, setIsUserNearBottom] = useState(true);
  const [shortcuts, setShortcuts] = useState<Shortcut[]>([]);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const { photo: clientPhoto, handleImageError: handleClientPhotoError } = useClientAvatar({
    clienteId: conv?.id,
    photo: conv?.photo,
  });

  const getDisplayName = (data: any): string => {
    if (!data) return 'Cliente';
    if (data.apelido && String(data.apelido).trim() !== '') return data.apelido;
    return data.chat_name || data.name || 'Cliente';
  };

  const rawPhone = conv?.phone || conv?.phoneNumber;
  const isBlocked = blockedOverride ?? !!conv?.blocked;

  const {
    isRecording,
    recordingMs,
    audioBlob,
    audioUrl,
    audioError,
    isAudioSending,
    showAudioPanel,
    handleStartRecording,
    handleStopRecording,
    handleCancelAudio,
    handleSendAudio,
    handleToggleAudioPanel,
    imageFile,
    imageUrl,
    imageError,
    isImageSending,
    showImagePanel,
    fileInputRef,
    handleImageSelected,
    handlePickPhoto,
    handleSendImage,
    handleCancelImage,
    handleToggleImagePanel,
  } = useChatMedia({
    phone: rawPhone,
    conversationId: conv?.id,
    resetKey: conv?.id,
    onMediaSent: async () => {
      const conversationId = conv.id;
      const data = await fetchMessages(conversationId);
      if (activeConversationIdRef.current === conversationId) receiveMessages(data);
    },
  });

  const {
    isImprovementOpen,
    improvementMessage,
    improvementText,
    setImprovementText,
    improvementError,
    improvementSuccess,
    isImprovementSending,
    improvementKind,
    handleOpenBotImprovement,
    handleOpenClientImprovement,
    handleCloseImprovement,
    handleSubmitImprovement,
  } = useMessageImprovements({ resetKey: conv?.id });

  // Atualiza isBotLocal / nome / bloqueio se conv mudar
  useEffect(() => {
    setIsBotLocal(conv?.type === "bot");
    const name = getDisplayName(conv);
    setDisplayName(name);
    setEditedName(name);
    setIsEditingName(false);
    setBlockedOverride(null);
  }, [conv]);

  // Pool de mensagens
  useEffect(() => {
    let mounted = true;
    let requestId = 0;
    let latestResponseId = 0;
    const load = async (notifyUser: boolean) => {
      const currentRequestId = ++requestId;
      try {
        const data = await fetchMessages(conv.id);
        if (mounted && currentRequestId >= latestResponseId) {
          latestResponseId = currentRequestId;
          receiveMessages(data);
        }
      } catch (error) {
        console.error('Erro ao carregar mensagens:', error);
        if (notifyUser && mounted) {
          toast.error(getApiErrorMessage(error, 'Não foi possível carregar as mensagens.'));
        }
      }
    };
    load(true);
    const interval = setInterval(() => load(false), CONVERSATION_MESSAGES_POLL_MS);
    return () => { mounted = false; clearInterval(interval); };
  }, [conv, receiveMessages]);

  // Detectar se usuário está próximo do final para controlar auto-scroll
  const handleScroll = () => {
    if (messagesContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100; // 100px threshold
      setIsUserNearBottom(isNearBottom);
    }
  };

  // Configurar listener de scroll
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      // Verificar posição inicial
      handleScroll();
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, []);

  useEffect(() => {
    if (!conv?.id) return;
    markAsSeen(conv.id).catch(() => {});
  }, [conv]);

  useEffect(() => {
    if (isUserNearBottom) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isUserNearBottom]);

  useEffect(() => {
    const fetchShortcutsData = async () => {
      try {
        const data = await fetchShortcuts(0);
        setShortcuts(data);
      } catch (error) {
        console.error('Error fetching shortcuts:', error);
      }
    };
    fetchShortcutsData();
  }, []);

  // Trocar atendimento para humano
  const handleSwitchToHuman = async () => {
    setIsSwitching(true);
    try {
      await switchToHuman(conv.id);
      setIsBotLocal(false);
    } catch {
      toast.error("Erro ao migrar para atendimento humano.");
    }
    setIsSwitching(false);
  };

  const handleSaveName = async (e?: React.MouseEvent) => {
    e?.stopPropagation();
    try {
      const nicknameToSave = editedName.trim() === '' ? null : editedName.trim();
      await updateClientNickname(conv.id.toString(), nicknameToSave);
      const finalName = nicknameToSave || getDisplayName({ ...conv, apelido: null });
      setDisplayName(finalName);
      setIsEditingName(false);
    } catch (error) {
      console.error('Error updating nickname:', error);
      toast.error("Erro ao atualizar o nome.");
    }
  };

  const handleEditKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSaveName();
    } else if (e.key === 'Escape') {
      setIsEditingName(false);
      setEditedName(displayName);
    }
  };

  const handleToggleBlocked = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!rawPhone) {
      toast.warning("Telefone não encontrado para este contato.");
      return;
    }
    setIsBlocking(true);
    try {
      if (isBlocked) {
        const removed = await unblockContactByPhone(rawPhone);
        if (!removed) {
          toast.warning("Não foi possível reativar. Contato não encontrado na lista de bloqueados.");
        } else {
          setBlockedOverride(false);
        }
      } else {
        await blockContactByPhone(rawPhone, displayName);
        setBlockedOverride(true);
      }
    } catch (error) {
      console.error('Error toggling blocked contact:', error);
      toast.error("Erro ao atualizar o bloqueio do contato.");
    }
    setIsBlocking(false);
  };

  // Envio de mensagem
  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || isSending) return;
    const content = newMessage;
    const pendingId = addPendingMessage(conv.id, content);
    setNewMessage('');
    setIsUserNearBottom(true);
    setIsSending(true);
    try {
      const sentMessage = await sendMessage(conv.id, content);
      confirmMessage(pendingId, { ...sentMessage, id: String(sentMessage.id), conversationId: conv.id, fromMe: true });
      // Sempre rolar para baixo após enviar mensagem
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        inputRef.current?.focus();
      }, 100);
    } catch {
      removePendingMessage(pendingId);
      setNewMessage((current) => current || content);
      toast.error("Erro ao enviar mensagem.");
    }
    setIsSending(false);
  };

  const filteredShortcuts = useMemo(() => {
    if (!newMessage.startsWith('/')) return [];
    return shortcuts.filter(s => s.atalho.toLowerCase().startsWith(newMessage.slice(1).toLowerCase()));
  }, [newMessage, shortcuts]);

  useEffect(() => {
    if (showShortcuts && filteredShortcuts.length > 0) {
      setHighlightedIndex((prev) => {
        if (prev >= 0 && prev < filteredShortcuts.length) return prev;
        return 0;
      });
    } else {
      setHighlightedIndex(-1);
    }
  }, [filteredShortcuts, showShortcuts]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const shouldShow = value.startsWith('/') && value.length > 0;
    setNewMessage(value);
    setShowShortcuts(shouldShow);
    if (!shouldShow) setHighlightedIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const hasDropdown = showShortcuts && filteredShortcuts.length > 0;

    if (hasDropdown && e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev + 1) % filteredShortcuts.length);
      return;
    }

    if (hasDropdown && e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev - 1 + filteredShortcuts.length) % filteredShortcuts.length);
      return;
    }

    if (hasDropdown && e.key === 'Enter') {
      const shortcut = filteredShortcuts[highlightedIndex] || filteredShortcuts[0];
      if (shortcut) {
        e.preventDefault();
        setNewMessage(shortcut.mensagem);
        setShowShortcuts(false);
      }
      return;
    }

    if (e.key === ' ' || e.key === 'Enter') {
      const shortcut = shortcuts.find(s => `/${s.atalho}` === newMessage);
      if (shortcut) {
        e.preventDefault();
        setNewMessage(shortcut.mensagem);
        setShowShortcuts(false);
      }
    }
  };

  return (
    <div className="fixed inset-0 z-[9000] flex justify-end">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white w-full max-w-md h-full shadow-2xl flex flex-col animate-slide-in-right rounded-l-3xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex gap-3 items-center">
            <div className="flex items-center justify-center font-bold text-white text-lg w-12 h-12 rounded-full shadow-lg select-none bg-gradient-to-br from-[#8CA6FF] to-[#5C60F5] border-2 border-white overflow-hidden">
              {clientPhoto ? (
                <img
                  src={clientPhoto}
                  alt={conv.name || "Cliente"}
                  className="w-full h-full object-cover"
                  onError={handleClientPhotoError}
                />
              ) : (
                getAvatarInitial(displayName || conv.name)
              )}
            </div>
            <div>
              <div className="flex items-center gap-2">
                {isEditingName ? (
                  <>
                    <input
                      className="w-44 max-w-[180px] rounded-md border border-gray-300 bg-white px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                      value={editedName}
                      onChange={(e) => setEditedName(e.target.value)}
                      onKeyDown={handleEditKeyDown}
                      autoFocus
                    />
                    <button
                      onClick={handleSaveName}
                      className="p-1 rounded text-slate-500 hover:text-slate-700"
                      title="Salvar"
                      type="button"
                    >
                      <Save size={14} />
                    </button>
                  </>
                ) : (
                  <>
                    <div className="font-bold text-lg">{displayName}</div>
                    <button
                      onClick={() => setIsEditingName(true)}
                      className="p-1 rounded text-slate-400 hover:text-slate-600"
                      title="Editar nome"
                      type="button"
                    >
                      <Edit2 size={14} />
                    </button>
                  </>
                )}
                <button
                  onClick={handleToggleBlocked}
                  className={`p-1 rounded disabled:opacity-50 ${isBlocked ? "text-emerald-600 hover:text-emerald-700" : "text-slate-400 hover:text-red-600"}`}
                  title={isBlocked ? "Reativar atendimento automático" : "Desativar atendimento automático"}
                  type="button"
                  disabled={isBlocking}
                >
                  {isBlocked ? <Unlock size={14} /> : <Ban size={14} />}
                </button>
              </div>
              <div className="text-xs text-gray-400">{formatPhone(rawPhone)}</div>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded hover:bg-gray-100">
            <X size={22} />
          </button>
        </div>

        {/* Mensagens */}
        <div
          ref={messagesContainerRef}
          className="flex-1 p-6 overflow-y-auto"
          style={{
            backgroundColor: '#fffbf5',
            backgroundImage: "linear-gradient(rgba(255, 251, 245, 0.76), rgba(255, 251, 245, 0.76)), url('/background3.png')",
            backgroundSize: 'auto, 200px auto',
            backgroundRepeat: 'repeat',
          }}
        >
          {messages.length === 0 ? (
            <p className="text-center text-gray-400">Nenhuma mensagem ainda.</p>
          ) : (
            <div className="space-y-3">
              <ConversationMessages
                key={`${conv.id}-${isBotLocal ? 'bot' : 'manual'}`}
                messages={messages}
                messagesEndRef={messagesEndRef}
                onImprove={handleOpenBotImprovement}
                onTeachResponse={handleOpenClientImprovement}
                unreadCount={isBotLocal ? 0 : (conv.unreadCount ?? (conv.unseen ? 1 : 0))}
                unreadSeparatorMarginClassName="-mx-6"
              />
            </div>
          )}
        </div>

        {/* Input ou botão para migrar para humano */}
        {isBotLocal ? (
          <div className="p-4 border-t flex flex-col items-center bg-white">
            <button
              disabled={isSwitching}
              onClick={handleSwitchToHuman}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-[#6366F1] text-white font-bold text-base shadow hover:bg-[#3730a3] transition"
            >
              <User2 size={20} />
              Migrar para atendimento humano
            </button>
            <span className="text-xs mt-2 text-gray-400 text-center">
              Só é possível enviar mensagens quando o atendimento for humano.
            </span>
          </div>
        ) : (
          <form onSubmit={handleSend} className="p-4 border-t bg-white">
            <div className="flex flex-col gap-3">
              {showImagePanel && (
                <div className="flex flex-col gap-2">
                  <div className="flex flex-wrap items-center gap-2">
                    {!imageFile && (
                      <button
                        type="button"
                        onClick={handlePickPhoto}
                        disabled={isImageSending}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200 transition disabled:opacity-50"
                      >
                        <Camera size={16} />
                        Foto
                      </button>
                    )}
                    {imageFile && (
                      <>
                        <button
                          type="button"
                          onClick={handleSendImage}
                          disabled={isImageSending}
                          className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition disabled:opacity-50"
                        >
                          {isImageSending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                          Enviar
                        </button>
                        <button
                          type="button"
                          onClick={handleCancelImage}
                          disabled={isImageSending}
                          className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 transition disabled:opacity-50"
                        >
                          <Trash2 size={16} />
                          Cancelar
                        </button>
                      </>
                    )}
                    {!imageFile && (
                      <span className="text-xs text-slate-400">Máx: 8MB</span>
                    )}
                  </div>

                  {imageUrl && (
                    <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                      <img src={imageUrl} alt="Prévia" className="max-h-40 w-auto rounded-md object-contain" />
                    </div>
                  )}

                  {imageError && (
                    <div className="text-xs text-red-500">{imageError}</div>
                  )}
                </div>
              )}

              {showAudioPanel && (
                <div className="flex flex-col gap-2">
                  <div className="flex flex-wrap items-center gap-2">
                    {!isRecording && !audioBlob && (
                      <button
                        type="button"
                        onClick={handleStartRecording}
                        disabled={isAudioSending}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200 transition disabled:opacity-50"
                      >
                        <Mic size={16} />
                        Gravar
                      </button>
                    )}
                    {isRecording && (
                      <button
                        type="button"
                        onClick={handleStopRecording}
                        className="inline-flex h-9 items-center justify-center gap-2 rounded-lg bg-rose-600 px-3 text-xs font-semibold text-white transition hover:bg-rose-700"
                      >
                        <Square size={16} />
                        Parar
                      </button>
                    )}
                    {audioBlob && !isRecording && (
                      <>
                        <button
                          type="button"
                          onClick={handleSendAudio}
                          disabled={isAudioSending}
                          className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition disabled:opacity-50"
                        >
                          {isAudioSending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                          Enviar
                        </button>
                        <button
                          type="button"
                          onClick={handleCancelAudio}
                          disabled={isAudioSending}
                          className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 transition disabled:opacity-50"
                        >
                          <Trash2 size={16} />
                          Cancelar
                        </button>
                      </>
                    )}
                    {isRecording && (
                      <span className="text-xs text-red-500 font-medium">
                        Gravando... {formatDuration(recordingMs)} / {formatDuration(MAX_AUDIO_DURATION_MS)}
                      </span>
                    )}
                    {!isRecording && !audioBlob && (
                      <span className="text-xs text-slate-400">Máx: 60s</span>
                    )}
                  </div>

                  {audioUrl && !isRecording && (
                    <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                      <audio controls src={audioUrl} className="w-full" />
                    </div>
                  )}

                  {audioError && (
                    <div className="text-xs text-red-500">{audioError}</div>
                  )}
                </div>
              )}

              <div className="relative flex items-center gap-2">
                <div className="relative flex-1">
                  {showShortcuts && filteredShortcuts.length > 0 && (
                    <div className="absolute bottom-full mb-2 bg-white border border-gray-300 rounded-lg shadow-lg max-h-40 overflow-y-auto w-full z-10">
                      {filteredShortcuts.map((shortcut, index) => (
                        <div
                          key={shortcut.id}
                          className={`px-4 py-2 cursor-pointer ${index === highlightedIndex ? 'bg-gray-100' : 'hover:bg-gray-100'}`}
                          onClick={() => {
                            setNewMessage(shortcut.mensagem);
                            setShowShortcuts(false);
                            inputRef.current?.focus();
                          }}
                          onMouseEnter={() => setHighlightedIndex(index)}
                        >
                          <span className="font-medium">/{shortcut.atalho}</span> - <span className="text-sm text-gray-500">{shortcut.mensagem.length > 50 ? shortcut.mensagem.slice(0, 50) + '...' : shortcut.mensagem}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <input
                    ref={inputRef}
                    className="w-full px-3 py-2 pr-16 rounded-lg border focus:outline-none"
                    placeholder="Digite sua mensagem..."
                    value={newMessage}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    disabled={isSending}
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={handleToggleImagePanel}
                    className={`absolute right-9 top-1/2 -translate-y-1/2 p-1 rounded-full transition ${
                      showImagePanel ? "bg-emerald-100 text-emerald-600" : "text-slate-400 hover:text-slate-600"
                    }`}
                    title="Enviar imagem"
                  >
                    <Camera size={18} />
                  </button>
                  <button
                    type="button"
                    onClick={handleToggleAudioPanel}
                    className={`absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full transition ${
                      showAudioPanel ? "bg-emerald-100 text-emerald-600" : "text-slate-400 hover:text-slate-600"
                    }`}
                    title="Enviar áudio"
                  >
                    <Mic size={18} />
                  </button>
                </div>
                <button
                  type="submit"
                  disabled={isSending || !newMessage.trim()}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-teal-500 text-white transition hover:bg-[#2f7d72] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <Send size={18} className="-translate-x-1 rotate-45" />
                </button>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleImageSelected}
              />
            </div>
          </form>
        )}
      </div>
      {isImprovementOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center px-4 py-6">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={handleCloseImprovement}
          />
          <div
            className="relative w-full max-w-2xl overflow-hidden rounded-3xl bg-white shadow-2xl ring-1 ring-black/5"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-6 py-5">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">
                  {improvementKind === 'client'
                    ? 'Ensinar resposta da IA'
                    : 'Corrigir/Melhorar resposta da IA'}
                </h3>
                <p className="text-sm text-slate-500">
                  {improvementKind === 'client'
                    ? 'Descreva como a IA deveria responder essa mensagem do cliente.'
                    : 'Explique como essa resposta poderia ser melhorada ou corrigida. Isso vira uma regra de atendimento para melhorar futuras mensagens.'}
                </p>
              </div>
              <button
                type="button"
                onClick={handleCloseImprovement}
                className="rounded-full p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition"
                aria-label="Fechar modal"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSubmitImprovement} className="px-6 py-5 space-y-4">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                  {improvementKind === 'client' ? 'Mensagem do cliente' : 'Mensagem da IA'}
                </div>
                <p className="text-sm text-slate-800 whitespace-pre-line">
                  {improvementMessage?.content || ''}
                </p>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700">
                  {improvementKind === 'client'
                    ? 'Como a IA deveria responder essa mensagem?'
                    : 'Como você corrigiria ou melhoraria essa resposta?'}
                </label>
                <textarea
                  className="min-h-[140px] w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 shadow-sm focus:border-teal-400 focus:outline-none focus:ring-2 focus:ring-teal-200"
                  placeholder={improvementKind === 'client'
                    ? 'Ex.: responda com cordialidade, confirme detalhes, ofereça próximos passos...'
                    : 'Ex.: seja mais direto, peça uma informação antes de responder, ajuste o tom...'}
                  value={improvementText}
                  onChange={(event) => setImprovementText(event.target.value)}
                  disabled={isImprovementSending}
                />
                <div className="text-xs text-slate-400">
                  Seja específico: descreva como a IA deveria responder de forma mais útil e clara.
                </div>
              </div>

              {improvementError && (
                <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
                  {improvementError}
                </div>
              )}
              {improvementSuccess && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
                  {improvementSuccess}
                </div>
              )}

              <div className="flex flex-wrap items-center justify-end gap-3 border-t border-slate-100 pt-4">
                <button
                  type="button"
                  onClick={handleCloseImprovement}
                  className="px-4 py-2 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-100 transition"
                  disabled={isImprovementSending}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isImprovementSending || !improvementText.trim()}
                  className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-[#2f7d72] px-4 text-sm font-semibold text-white shadow-lg shadow-[#2f7d72]/25 transition hover:bg-[#27685f] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isImprovementSending ? <Loader2 size={16} className="animate-spin" /> : null}
                  {improvementKind === 'client' ? 'Enviar resposta' : 'Enviar correção'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <style>{`
        .animate-slide-in-right { animation: slideInRight 0.3s cubic-bezier(.21,.68,.68,1.05);}
        @keyframes slideInRight { 0% {transform: translateX(80px); opacity: 0;} 100%{transform: translateX(0); opacity:1;}}
      `}</style>
    </div>
  );
};

export default SideModal;
